import React, { useCallback, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import {
    type QuestionnaireError,
    isQuestionnaireError
} from 'ts/common/components/questionnaire/Questionnaire';
import { useTranslate } from 'ts/common/hooks';
import { FetchMethod, useClientApiFetch } from 'client_react/bootstrap';

interface Props {
    questionnairePublicId: string;
    setQuestionnaire: React.Dispatch<
        React.SetStateAction<
            SpApi.Client.IQuestionnaireMinimal | SpApi.Client.IQuestionnaire | undefined
        >
    >;
}

const useSaveAndSubmitQuestionnaire = ({ questionnairePublicId, setQuestionnaire }: Props) => {
    const t = useTranslate('clientQuestionnaire');
    const toast = useToast();
    const [error, setError] = useState<QuestionnaireError | null>(null);
    const [savingFailed, setSavingFailed] = useState(false);

    const { loading: saving, performFetch: patchQuestionnaire } = useClientApiFetch<unknown>(
        `questionnaire/${questionnairePublicId}`,
        {
            defer: true,
            method: FetchMethod.PATCH
        }
    );

    const saveQuestionResponse = useCallback(
        async (questionId: number, responseText: string | null) => {
            const email = sessionStorage.getItem(`questionnaire-email-${questionnairePublicId}`);

            const response = await patchQuestionnaire({
                data: { id: questionId, response_text: responseText },
                search: email ? { email } : undefined
            });

            if (response && !isQuestionnaireError(response)) {
                setError(null);
                setSavingFailed(false);

                setQuestionnaire((prev) => {
                    if (!prev || !('pages' in prev)) {
                        return prev;
                    }

                    return {
                        ...prev,
                        questionnaireStatus: 'partial',
                        pages: prev.pages.map((page) => ({
                            ...page,
                            questions: page.questions.map((q) =>
                                q.id === questionId ? { ...q, responseText } : q
                            )
                        }))
                    };
                });
            } else {
                setError(response);
                setSavingFailed(true);
                toast({
                    title: t('error'),
                    description: response?.detail ?? t('errorSavingQuestionnaire'),
                    status: 'error'
                });
            }
        },
        [patchQuestionnaire, questionnairePublicId, setQuestionnaire, toast, t]
    );

    const { loading: submitting, performFetch: postSubmission } = useClientApiFetch<unknown>(
        `questionnaire/${questionnairePublicId}/submission`,
        {
            defer: true,
            method: FetchMethod.POST
        }
    );

    const submitQuestionnaire = useCallback(async () => {
        const email = sessionStorage.getItem(`questionnaire-email-${questionnairePublicId}`);

        if (!email) {
            toast({
                title: t('error'),
                description: t('emailHeading'),
                status: 'error'
            });
            return;
        }

        const response = await postSubmission({
            search: { email }
        });

        if (response && !isQuestionnaireError(response)) {
            setError(null);
            setSavingFailed(false);
            toast({
                title: t('submitSuccess'),
                description: t('submitSuccessMessage'),
                status: 'success'
            });

            setQuestionnaire((prev) => {
                if (!prev || !('pages' in prev)) {
                    return prev;
                }

                return {
                    ...prev,
                    questionnaireStatus: 'completed',
                    completed: new Date().toISOString()
                };
            });
        } else {
            const description =
                response && 'info' in response && Array.isArray(response.info)
                    ? response.info.join(' ')
                    : response?.detail ?? t('errorSavingQuestionnaire');
            setError(response);
            setSavingFailed(true);
            toast({
                title: t('error'),
                description,
                status: 'error'
            });
        }
    }, [postSubmission, questionnairePublicId, setQuestionnaire, toast, t]);

    return {
        saveQuestionResponse,
        submitQuestionnaire,
        saving,
        submitting,
        error,
        savingFailed
    };
};

export default useSaveAndSubmitQuestionnaire;
